import { useEffect, useState } from "react";
import AppShell from "../components/app/AppShell";
import SEOHead from "../components/app/SEOHead";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { Copy, Link as LinkIcon, Wallet, Users, ChartLineUp, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Affiliate() {
  const { t, lang } = useT();
  const [profile, setProfile] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [applyForm, setApplyForm] = useState({ name: "", contact: "", audience: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/affiliate/me");
      setProfile(data);
      if (data.is_affiliate) {
        const { data: cs } = await api.get("/affiliate/commissions");
        setCommissions(cs);
      }
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!applyForm.name.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/affiliate/apply", applyForm);
      toast.success(t("affiliate.application_submitted"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("affiliate.application_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!profile?.referral_link) return;
    navigator.clipboard.writeText(profile.referral_link)
      .then(() => toast.success(t("affiliate.link_copied")))
      .catch(() => toast.error(t("common.failed")));
  };

  if (!profile) {
    return <AppShell><div className="h-64 grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell>
      <SEOHead pageKey="default" />
      <div className="overline text-[#002FA7]">{t("affiliate.overline")}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("affiliate.title")}</h1>
      <p className="text-slate-600 mt-2 max-w-2xl">{t("affiliate.subtitle")}</p>

      {/* ===== NOT APPROVED YET ===== */}
      {!profile.is_affiliate && profile.status !== "pending" && (
        <div className="mt-8 card max-w-2xl" data-testid="affiliate-apply-card">
          <div className="overline">{t("affiliate.apply_form_title")}</div>
          <p className="text-sm text-slate-600 mt-2">{t("affiliate.apply_intro")}</p>
          <ul className="mt-3 text-sm text-slate-700 space-y-1.5">
            <li>• {t("affiliate.benefit_1", { rate: Math.round(profile.first_rate * 100) })}</li>
            <li>• {t("affiliate.benefit_2", { rate: Math.round(profile.recurring_rate * 100) })}</li>
            <li>• {t("affiliate.benefit_3")}</li>
          </ul>
          <form onSubmit={submitApplication} className="mt-5 space-y-3">
            <input
              required
              placeholder={t("affiliate.name_placeholder")}
              value={applyForm.name}
              onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
              className="w-full"
              data-testid="affiliate-apply-name"
            />
            <input
              placeholder={t("affiliate.contact_placeholder")}
              value={applyForm.contact}
              onChange={(e) => setApplyForm({ ...applyForm, contact: e.target.value })}
              className="w-full"
              data-testid="affiliate-apply-contact"
            />
            <input
              placeholder={t("affiliate.audience_placeholder")}
              value={applyForm.audience}
              onChange={(e) => setApplyForm({ ...applyForm, audience: e.target.value })}
              className="w-full"
              data-testid="affiliate-apply-audience"
            />
            <textarea
              rows={3}
              placeholder={t("affiliate.notes_placeholder")}
              value={applyForm.notes}
              onChange={(e) => setApplyForm({ ...applyForm, notes: e.target.value })}
              className="w-full"
              data-testid="affiliate-apply-notes"
            />
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="affiliate-apply-submit">
              <PaperPlaneTilt size={16} weight="bold" />
              <span>{submitting ? t("affiliate.submitting") : t("affiliate.submit_button")}</span>
            </button>
          </form>
        </div>
      )}

      {/* ===== PENDING ===== */}
      {profile.status === "pending" && (
        <div className="mt-8 card max-w-2xl bg-amber-50 border-amber-200" data-testid="affiliate-pending-card">
          <div className="overline text-amber-700">{t("affiliate.pending_overline")}</div>
          <h2 className="font-display font-bold mt-1">{t("affiliate.pending_title")}</h2>
          <p className="text-sm text-slate-700 mt-2">{t("affiliate.pending_body")}</p>
        </div>
      )}

      {/* ===== APPROVED — DASHBOARD ===== */}
      {profile.is_affiliate && (
        <>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={Users} label={t("affiliate.total_referrals")} value={profile.total_referrals} testid="affiliate-stat-referrals" />
            <StatCard icon={ChartLineUp} label={t("affiliate.paying_referrals")} value={profile.total_paying_referrals} testid="affiliate-stat-paying" />
            <StatCard icon={Wallet} label={t("affiliate.total_earned")} value={`${profile.total_earned_try.toFixed(2)}₺`} sub={`${profile.total_pending_try.toFixed(2)}₺ ${t("affiliate.pending")}`} testid="affiliate-stat-earned" />
          </div>

          <div className="mt-6 card" data-testid="affiliate-link-card">
            <div className="overline flex items-center gap-1.5"><LinkIcon size={12} weight="fill" /> {t("affiliate.your_link")}</div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2 items-stretch">
              <input
                readOnly
                value={profile.referral_link || ""}
                className="flex-1 font-mono text-sm"
                onClick={(e) => e.target.select()}
                data-testid="affiliate-link-input"
              />
              <button onClick={copyLink} className="btn-primary !py-2.5" data-testid="affiliate-link-copy">
                <Copy size={16} weight="bold" />
                <span>{t("affiliate.copy")}</span>
              </button>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              {t("affiliate.code_label")}: <span className="font-mono font-bold text-[#002FA7]">{profile.code}</span> ·{" "}
              {t("affiliate.first_rate")}: <strong>%{Math.round(profile.first_rate * 100)}</strong> ·{" "}
              {t("affiliate.recurring_rate")}: <strong>%{Math.round(profile.recurring_rate * 100)}</strong>
            </div>
          </div>

          <div className="mt-6 card" data-testid="affiliate-commissions-card">
            <div className="overline">{t("affiliate.recent_commissions")}</div>
            {commissions.length === 0 ? (
              <div className="mt-3 flex flex-col sm:flex-row items-start gap-3">
                <p className="text-sm text-slate-500 flex-1">{t("affiliate.no_commissions")}</p>
                <button onClick={copyLink} className="btn-secondary !py-2 text-sm whitespace-nowrap" data-testid="affiliate-empty-copy">
                  <Copy size={14} weight="bold" />
                  <span>{t("affiliate.copy")}</span>
                </button>
              </div>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm" data-testid="affiliate-commissions-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">{t("affiliate.col_date")}</th>
                      <th className="py-2 pr-3">{t("affiliate.col_referred")}</th>
                      <th className="py-2 pr-3">{t("affiliate.col_plan")}</th>
                      <th className="py-2 pr-3">{t("affiliate.col_gross")}</th>
                      <th className="py-2 pr-3">{t("affiliate.col_rate")}</th>
                      <th className="py-2 pr-3">{t("affiliate.col_commission")}</th>
                      <th className="py-2 pr-3">{t("affiliate.col_status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c) => (
                      <tr key={c.commission_id} className="border-b border-slate-100">
                        <td className="py-2 pr-3 text-slate-600">{new Date(c.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{c.referred_email}</td>
                        <td className="py-2 pr-3 capitalize">{c.plan}</td>
                        <td className="py-2 pr-3">{c.gross_try.toFixed(2)}₺</td>
                        <td className="py-2 pr-3">%{Math.round(c.rate * 100)}</td>
                        <td className="py-2 pr-3 font-bold text-[#002FA7]">{c.commission_try.toFixed(2)}₺</td>
                        <td className="py-2 pr-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${c.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                            {c.status === "paid" ? t("affiliate.status_paid") : t("affiliate.status_pending")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </AppShell>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={16} weight="fill" className="text-[#002FA7]" />
      </div>
      <div className="font-display text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
